import React, { useState } from 'react';
import { apiService } from '../services/api';
import { isLeagueCreationAllowed } from '../config/leagueCreation';

interface CreateLeagueModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournamentId?: number;
  tournamentName?: string;
  onLeagueCreated: () => void;
}

const CreateLeagueModal: React.FC<CreateLeagueModalProps> = ({
  isOpen,
  onClose,
  tournamentId,
  tournamentName,
  onLeagueCreated
}) => {
  const [formData, setFormData] = useState({
    name: '',
    league_type: 'pickem',
    max_participants: 10,
    is_public: false,
    selected_tournament_id: tournamentId || 0,
    h2h_type: '' as '' | 'single_match' | 'multi_match',
    h2h_single_match_id: '',
    h2h_series_length: null as number | null
  });
  const [maxParticipantsInput, setMaxParticipantsInput] = useState('10');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tournaments, setTournaments] = useState<any[]>([]);
  const [loadingTournaments, setLoadingTournaments] = useState(false);
  const [h2hSettings, setH2hSettings] = useState<{ h2h_enabled: boolean; h2h_allowed_modes: string[]; h2h_allowed_lengths: number[] } | null>(null);
  const [upcomingMatches, setUpcomingMatches] = useState<{ id: string; name: string; match_date?: string }[]>([]);
  const [loadingH2h, setLoadingH2h] = useState(false);

  // Fetch tournaments when modal opens and no specific tournament is selected
  React.useEffect(() => {
    if (isOpen && !tournamentId) {
      fetchTournaments();
    }
  }, [isOpen, tournamentId]);

  // Reset disallowed league type when modal opens (e.g. draft was previously selected)
  React.useEffect(() => {
    if (!isOpen) return;
    setFormData((prev) => {
      if (isLeagueCreationAllowed(prev.league_type)) return prev;
      return {
        ...prev,
        league_type: 'pickem',
        max_participants: 10,
        h2h_type: '',
        h2h_single_match_id: '',
        h2h_series_length: null
      };
    });
  }, [isOpen]);

  const tid = formData.selected_tournament_id || tournamentId;
  React.useEffect(() => {
    if (!isOpen || formData.league_type !== 'h2h' || !tid) {
      setH2hSettings(null);
      setUpcomingMatches([]);
      return;
    }
    const tournamentIdNum = typeof tid === 'number' ? tid : parseInt(String(tid), 10);
    if (isNaN(tournamentIdNum)) return;
    setLoadingH2h(true);
    Promise.all([
      apiService.getTournamentH2hSettingsForLeagueCreation(tournamentIdNum).catch(() => ({ h2h_enabled: false, h2h_allowed_modes: ['single', 'multi'], h2h_allowed_lengths: [3, 5, -1] })),
      apiService.getMatches(tournamentIdNum).catch(() => ({ matches: [] }))
    ]).then(([h2hRes, matchesRes]) => {
      const res = h2hRes as any;
      setH2hSettings({
        h2h_enabled: !!res?.h2h_enabled,
        h2h_allowed_modes: Array.isArray(res?.h2h_allowed_modes) ? res.h2h_allowed_modes : ['single', 'multi'],
        h2h_allowed_lengths: Array.isArray(res?.h2h_allowed_lengths) ? res.h2h_allowed_lengths : [3, 5, -1]
      });
      const list = (matchesRes as any)?.matches ?? [];
      const now = new Date();
      const upcoming = list.filter((m: any) => {
        const status = m?.status ?? m?.match_status;
        if (status === 'completed' || status === 'live') return false;
        // Prefer full datetime so "next match" (e.g. today 13:30) is not excluded when API sends date-only (midnight UTC)
        const d = m?.match_datetime ?? m?.match_date ?? m?.dateTimeGMT;
        if (!d) return true;
        const md = new Date(d);
        return !isNaN(md.getTime()) && md > now;
      });
      setUpcomingMatches(upcoming.map((m: any) => ({ id: String(m?.id ?? m?.match_id ?? ''), name: m?.name ?? m?.match_name ?? 'Match', match_date: m?.match_date ?? m?.dateTimeGMT })));
    }).finally(() => setLoadingH2h(false));
  }, [isOpen, formData.league_type, formData.selected_tournament_id, tournamentId]);

  const fetchTournaments = async () => {
    try {
      setLoadingTournaments(true);
      const data = await apiService.getLiveTournaments();
      
      if (data.success) {
        setTournaments(data.liveTournaments || []);
      }
    } catch (err) {
      console.error('Error fetching tournaments:', err);
    } finally {
      setLoadingTournaments(false);
    }
  };

  const validateForm = () => {
    const normalizedParticipants = maxParticipantsInput.trim();
    if (!/^\d+$/.test(normalizedParticipants)) {
      setError('Number of participants must be a positive whole number');
      return false;
    }
    const parsedParticipants = parseInt(normalizedParticipants, 10);
    if (!Number.isFinite(parsedParticipants) || parsedParticipants <= 0) {
      setError('Number of participants must be a positive whole number');
      return false;
    }

    // Basic validation
    if (!formData.name.trim()) {
      setError('League name is required');
      return false;
    }
    if (!formData.league_type) {
      setError('League type is required');
      return false;
    }
    if (!isLeagueCreationAllowed(formData.league_type)) {
      setError('This league type cannot be created here.');
      return false;
    }
    if (!formData.selected_tournament_id) {
      setError('Tournament selection is required');
      return false;
    }
    if (parsedParticipants < 2) {
      setError('Number of participants must be at least 2');
      return false;
    }

    if (formData.league_type === 'h2h') {
      if (parsedParticipants !== 2) {
        setError('H2H leagues must have exactly 2 participants');
        return false;
      }
      if (!formData.h2h_type) {
        setError('Select Single match or Multi match for H2H');
        return false;
      }
      if (formData.h2h_type === 'single_match' && !formData.h2h_single_match_id) {
        setError('Select a match for single-match H2H');
        return false;
      }
      if (formData.h2h_type === 'multi_match' && h2hSettings && !h2hSettings.h2h_enabled) {
        setError('H2H is not enabled for this tournament');
        return false;
      }
    }
    if (formData.league_type === 'draft_league') {
      if (parsedParticipants > 16) {
        setError('Draft leagues cannot have more than 16 participants');
        return false;
      }
      if (parsedParticipants % 2 !== 0) {
        setError('Draft leagues must have an even number of participants');
        return false;
      }
    }
    if (['pickem', 'budget_league', 'eliminator'].includes(formData.league_type) && formData.is_public && parsedParticipants > 1000) {
      setError('Public leagues cannot have more than 1000 participants');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    console.log('🔍 CreateLeagueModal: Starting league creation');
    console.log('🔍 CreateLeagueModal: Tournament ID:', tournamentId);
    console.log('🔍 CreateLeagueModal: Form data:', formData);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      // Get auth token from localStorage
      const token = localStorage.getItem('authToken');
      console.log('🔍 CreateLeagueModal: Token exists:', !!token);
      
      if (!token) {
        console.log('🔍 CreateLeagueModal: No token found, showing error');
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }

      console.log('🔍 CreateLeagueModal: Making API request to create league');
      const parsedParticipants = parseInt(maxParticipantsInput.trim(), 10);
      const payload: any = {
        name: formData.name,
        live_tournament_id: formData.selected_tournament_id,
        league_type: formData.league_type,
        max_participants: parsedParticipants,
        is_public: formData.is_public
      };
      if (formData.league_type === 'h2h') {
        payload.h2h_type = formData.h2h_type;
        payload.h2h_single_match_id = formData.h2h_type === 'single_match' ? formData.h2h_single_match_id || null : null;
        payload.h2h_series_length = formData.h2h_type === 'multi_match' ? (formData.h2h_series_length ?? null) : null;
      }
      const data = await apiService.createFantasyLeague(payload);

      console.log('🔍 CreateLeagueModal: Response data:', data);

      if (data.success) {
        const league = data.fantasyLeague;
        let message = 'League created successfully!';
        
        // If it's a private league, show the join code
        if (!formData.is_public && league.join_code) {
          message += `\n\nJoin Code: ${league.join_code}\n\nShare this code with others to let them join your league!`;
        }
        
        alert(message);
        onLeagueCreated();
        onClose();
        setFormData({
          name: '',
          league_type: 'pickem',
          max_participants: 10,
          is_public: false,
          selected_tournament_id: tournamentId || 0,
          h2h_type: '',
          h2h_single_match_id: '',
          h2h_series_length: null
        });
        setMaxParticipantsInput('10');
      } else {
        setError(data.message || 'Failed to create league');
      }
    } catch (err) {
      console.error('🔍 CreateLeagueModal: Error creating league:', err);
      setError('Error creating league. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      };
      
      // Auto-adjust participant count based on league type
      if (name === 'league_type') {
        if (!isLeagueCreationAllowed(String(newValue))) {
          return prev;
        }
        if (newValue === 'h2h') {
          updated.max_participants = 2;
          setMaxParticipantsInput('2');
        } else if (newValue === 'draft_league') {
          updated.max_participants = 8; // Default even number for draft
          setMaxParticipantsInput('8');
        } else {
          updated.max_participants = 10; // Default for other types
          setMaxParticipantsInput('10');
        }
      }
      
      return updated;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-white">Create Fantasy League</h3>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-2xl"
          >
            ×
          </button>
        </div>
        
        <p className="text-white/80 mb-4">Create a new fantasy league for {tournamentName}</p>
        
        {error && (
          <div className="bg-red-900/30 border border-red-500 text-red-300 px-3 py-2 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">League Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter league name"
              required
              className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Tournament Selection - Only show if no specific tournament is provided */}
          {!tournamentId && (
            <div>
              <label className="block text-sm font-medium text-white mb-2">Select Tournament *</label>
              {loadingTournaments ? (
                <div className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white">
                  Loading tournaments...
                </div>
              ) : (
                <select
                  name="selected_tournament_id"
                  value={formData.selected_tournament_id}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="0" className="text-gray-800">Choose a tournament</option>
                  {tournaments.map(tournament => (
                    <option key={tournament.id} value={tournament.id} className="text-gray-800">
                      {tournament.name} ({tournament.format} {tournament.year})
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-white mb-2">League Type *</label>
            <p className="text-xs text-white/50 mb-2">
              Pick&apos;em, Budget, and H2H are available. Draft and Eliminator are not offered in this form.
            </p>
            <select
              name="league_type"
              value={formData.league_type}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="pickem" className="text-gray-800">Pick&apos;em</option>
              <option value="budget_league" disabled={!isLeagueCreationAllowed('budget_league')} className="text-gray-800">
                Budget League
              </option>
              <option value="draft_league" disabled className="text-gray-800">
                Draft League (unavailable)
              </option>
              <option value="h2h" disabled={!isLeagueCreationAllowed('h2h')} className="text-gray-800">
                H2H
              </option>
              <option value="eliminator" disabled className="text-gray-800">
                Eliminator (unavailable)
              </option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">Number of Participants</label>
            <input
              type="text"
              name="max_participants"
              value={maxParticipantsInput}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') {
                  setMaxParticipantsInput('');
                  setFormData(prev => ({ ...prev, max_participants: 0 }));
                  return;
                }
                if (!/^\d+$/.test(raw)) return;
                const normalized = raw.replace(/^0+(?=\d)/, '');
                const parsed = parseInt(normalized, 10);
                setMaxParticipantsInput(normalized);
                setFormData(prev => ({ ...prev, max_participants: Number.isNaN(parsed) ? 0 : parsed }));
              }}
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Enter participants (e.g. 25)"
              disabled={formData.league_type === 'h2h'}
              className={`w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                formData.league_type === 'h2h' ? 'opacity-60 cursor-not-allowed' : ''
              }`}
            />
            <p className="text-xs text-white/60 mt-1">
              {formData.league_type === 'h2h' && 'H2H leagues must have exactly 2 participants (fixed)'}
              {formData.league_type === 'draft_league' && 'Draft leagues must have an even number of participants (max 16)'}
              {['pickem', 'budget_league', 'eliminator'].includes(formData.league_type) && formData.is_public && 'Public leagues can have up to 1000 participants'}
            </p>
          </div>

          {formData.league_type === 'h2h' && (
            <>
              <div>
                <label className="block text-sm font-medium text-white mb-2">H2H type *</label>
                <select
                  name="h2h_type"
                  value={formData.h2h_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, h2h_type: e.target.value as 'single_match' | 'multi_match', h2h_single_match_id: '', h2h_series_length: null }))}
                  required
                  className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="" className="text-gray-800">Select</option>
                  {h2hSettings && h2hSettings.h2h_allowed_modes.includes('single') && (
                    <option value="single_match" className="text-gray-800">Single match</option>
                  )}
                  {h2hSettings && h2hSettings.h2h_allowed_modes.includes('multi') && (
                    <option value="multi_match" className="text-gray-800">Multi match</option>
                  )}
                  {(!h2hSettings || loadingH2h) && (
                    <>
                      <option value="single_match" className="text-gray-800">Single match</option>
                      <option value="multi_match" className="text-gray-800">Multi match</option>
                    </>
                  )}
                </select>
              </div>
              {formData.h2h_type === 'single_match' && (
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Select match *</label>
                  {loadingH2h ? (
                    <div className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white">Loading matches...</div>
                  ) : (
                    <select
                      name="h2h_single_match_id"
                      value={formData.h2h_single_match_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, h2h_single_match_id: e.target.value }))}
                      required
                      className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="" className="text-gray-800">Upcoming matches</option>
                      {upcomingMatches.map(m => (
                        <option key={m.id} value={m.id} className="text-gray-800">{m.name}</option>
                      ))}
                      {upcomingMatches.length === 0 && !loadingH2h && <option value="" className="text-gray-800">No upcoming matches</option>}
                    </select>
                  )}
                </div>
              )}
              {formData.h2h_type === 'multi_match' && h2hSettings && (
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Series length</label>
                  <div className="flex flex-wrap gap-2">
                    {h2hSettings.h2h_allowed_lengths.includes(3) && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, h2h_series_length: prev.h2h_series_length === 3 ? null : 3 }))}
                        className={`px-3 py-2 rounded-lg text-sm font-medium ${formData.h2h_series_length === 3 ? 'bg-indigo-600 text-white' : 'bg-white/20 text-white border border-white/30'}`}
                      >
                        3 matches
                      </button>
                    )}
                    {h2hSettings.h2h_allowed_lengths.includes(5) && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, h2h_series_length: prev.h2h_series_length === 5 ? null : 5 }))}
                        className={`px-3 py-2 rounded-lg text-sm font-medium ${formData.h2h_series_length === 5 ? 'bg-indigo-600 text-white' : 'bg-white/20 text-white border border-white/30'}`}
                      >
                        5 matches
                      </button>
                    )}
                    {h2hSettings.h2h_allowed_lengths.includes(-1) && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, h2h_series_length: prev.h2h_series_length === -1 ? null : -1 }))}
                        className={`px-3 py-2 rounded-lg text-sm font-medium ${formData.h2h_series_length === -1 ? 'bg-indigo-600 text-white' : 'bg-white/20 text-white border border-white/30'}`}
                      >
                        Full tournament
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-white/60 mt-1">Optional: leave unselected for full tournament.</p>
                </div>
              )}
            </>
          )}

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="isPublic"
              name="is_public"
              checked={formData.is_public}
              onChange={handleInputChange}
              className="rounded"
            />
            <label htmlFor="isPublic" className="text-white">Public League</label>
          </div>

          <div className="flex space-x-2 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-green-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              {loading ? 'Creating...' : 'Create League'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateLeagueModal; 